import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldCheckIcon,
  QuestionMarkCircleIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ScaleIcon,
  BookOpenIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  ArrowRightIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { API_BASE_URL } from '../config/api';
import { exportAPI } from '../services/api';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

// US States for Medicaid selection
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming', 'District of Columbia'
];

interface InsuranceTerm {
  id: string;
  term: string;
  definition: string;
  category: string;
  example?: string;
}

interface TermExplanation {
  term: string;
  definition: string;
  how_it_works: string;
  example: string;
  key_points: string[];
  related_terms: string[];
  questions_to_ask: string[];
  additional_reading: { title: string; url: string; source: string; description?: string }[];
  disclaimer: string;
}

interface CoverageAnswer {
  question: string;
  answer: string;
  explanation: string;
  factors: string[];
  steps_to_take: string[];
  questions_to_ask: string[];
  disclaimer: string;
}

interface CostEstimate {
  procedure: string;
  overview: string;
  cost_ranges: string;
  cost_factors: string[];
  before_procedure: string[];
  questions_to_ask: string[];
  negotiation_tips: string[];
  disclaimer: string;
}

interface EOBInterpretation {
  summary: string;
  services: string;
  cost_breakdown: string;
  what_you_owe: string;
  looks_correct: string[];
  to_verify: string[];
  next_steps: string[];
  questions_to_ask: string[];
  disclaimer: string;
}

interface PatientRight {
  id: string;
  title: string;
  description: string;
  applies_to: string[];
  what_to_do: string;
}

interface PlanType {
  id: string;
  name: string;
  pros: string[];
  cons: string[];
  best_for: string;
}

interface CategorySuggestion {
  term: string;
  suggested_category: string;
  available_categories: string[];
  confidence: string;
  explanation: string;
}

type TabType = 'glossary' | 'questions' | 'costs' | 'eob' | 'rights';

export const InsuranceGuide: React.FC = () => {
  const { t, i18n } = useTranslation();

  // State
  const [activeTab, setActiveTab] = useState<TabType>('glossary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Glossary state
  const [terms, setTerms] = useState<InsuranceTerm[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTerm, setSelectedTerm] = useState<InsuranceTerm | null>(null);
  const [termExplanation, setTermExplanation] = useState<TermExplanation | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Questions state
  const [coverageQuestion, setCoverageQuestion] = useState('');
  const [planType, setPlanType] = useState('');
  const [coverageAnswer, setCoverageAnswer] = useState<CoverageAnswer | null>(null);

  // Cost state
  const [procedure, setProcedure] = useState('');
  const [hasMetDeductible, setHasMetDeductible] = useState(false);
  const [inNetwork, setInNetwork] = useState(true);
  const [costEstimate, setCostEstimate] = useState<CostEstimate | null>(null);

  // EOB state
  const [eobText, setEobText] = useState('');
  const [eobInterpretation, setEOBInterpretation] = useState<EOBInterpretation | null>(null);

  // Rights state
  const [rights, setRights] = useState<PatientRight[]>([]);
  const [planTypes, setPlanTypes] = useState<PlanType[]>([]);

  // Export state
  const [exportContent, setExportContent] = useState('');

  // Simple mode
  const [simpleMode, setSimpleMode] = useState(true);

  // Add to dictionary state
  const [isAddingTerm, setIsAddingTerm] = useState(false);
  const [addTermMessage, setAddTermMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categorySuggestion, setCategorySuggestion] = useState<CategorySuggestion | null>(null);
  const [selectedAddCategory, setSelectedAddCategory] = useState<string>('');
  const [isLoadingCategory, setIsLoadingCategory] = useState(false);

  // Medicaid state selection
  const [medicaidState, setMedicaidState] = useState('');
  const [showStateModal, setShowStateModal] = useState(false);

  // File upload state
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [policyText, setPolicyText] = useState('');
  const [eobFile, setEobFile] = useState<File | null>(null);

  // Refs
  const questionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const eobTextareaRef = useRef<HTMLTextAreaElement>(null);
  const policyFileInputRef = useRef<HTMLInputElement>(null);
  const eobFileInputRef = useRef<HTMLInputElement>(null);

  // Load initial data
  useEffect(() => {
    loadTerms();
    loadRights();
    loadPlanTypes();
  }, []);

  const loadTerms = async () => {
    try {
      // Fetch built-in terms
      const termsResponse = await fetch(`${API_BASE_URL}/insurance/terms`);
      let allTerms: InsuranceTerm[] = [];

      if (termsResponse.ok) {
        const termsData = await termsResponse.json();
        allTerms = termsData.all_terms || [];
      }

      // Also fetch user-added terms from the dictionary
      const dictResponse = await fetch(`${API_BASE_URL}/insurance/dictionary/categories?language=${i18n.language}`);
      if (dictResponse.ok) {
        const dictData = await dictResponse.json();
        // Add user-added terms (those with source: 'user') to the terms list
        if (dictData.categories) {
          for (const [category, terms] of Object.entries(dictData.categories)) {
            const categoryTerms = terms as Array<{ term: string; short_definition: string; category: string; source: string }>;
            for (const term of categoryTerms) {
              if (term.source === 'user') {
                // Check if term already exists in allTerms
                const exists = allTerms.some(t => t.term.toLowerCase() === term.term.toLowerCase());
                if (!exists) {
                  allTerms.push({
                    id: term.term.toLowerCase().replace(/\s+/g, '_'),
                    term: term.term,
                    definition: term.short_definition,
                    category: term.category
                  });
                }
              }
            }
          }
        }
      }

      setTerms(allTerms);
    } catch (err) {
      console.error('Failed to load terms:', err);
    }
  };

  const loadRights = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/insurance/rights`);
      if (response.ok) {
        const data = await response.json();
        setRights(data.rights || []);
      }
    } catch (err) {
      console.error('Failed to load rights:', err);
    }
  };

  const loadPlanTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/insurance/plan-types`);
      if (response.ok) {
        const data = await response.json();
        setPlanTypes(data.plan_types || []);
      }
    } catch (err) {
      console.error('Failed to load plan types:', err);
    }
  };

  const explainTerm = async (term: string) => {
    setLoading(true);
    setError(null);
    setTermExplanation(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/insurance/explain-term`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ term, simple_mode: simpleMode }),
      });

      if (!response.ok) {
        throw new Error('Failed to explain term');
      }

      const data = await response.json();
      setTermExplanation(data);
    } catch (err) {
      setError(t('insuranceGuide:errors.explainFailed'));
    } finally {
      setLoading(false);
    }
  };

  const askCoverageQuestion = async () => {
    if (!coverageQuestion.trim()) return;

    setLoading(true);
    setError(null);
    setCoverageAnswer(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/insurance/coverage-question`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          question: coverageQuestion,
          plan_type: planType || null,
          medicaid_state: planType === 'Medicaid' ? medicaidState : null,
          simple_mode: simpleMode,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to answer question');
      }

      const data = await response.json();
      setCoverageAnswer(data);
    } catch (err) {
      setError(t('insuranceGuide:errors.questionFailed'));
    } finally {
      setLoading(false);
    }
  };

  const estimateCost = async () => {
    if (!procedure.trim()) return;

    setLoading(true);
    setError(null);
    setCostEstimate(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/insurance/estimate-cost`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          procedure,
          plan_type: planType || null,
          has_met_deductible: hasMetDeductible,
          in_network: inNetwork,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to estimate cost');
      }

      const data = await response.json();
      setCostEstimate(data);
    } catch (err) {
      setError(t('insuranceGuide:errors.costFailed'));
    } finally {
      setLoading(false);
    }
  };

  const interpretEOB = async () => {
    const textToInterpret = eobFile ? await extractTextFromFile(eobFile) : eobText;
    if (!textToInterpret.trim()) return;

    setLoading(true);
    setError(null);
    setEOBInterpretation(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/insurance/interpret-eob`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          eob_text: textToInterpret,
          simple_mode: simpleMode,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to interpret EOB');
      }

      const data = await response.json();
      setEOBInterpretation(data);
    } catch (err) {
      setError(t('insuranceGuide:errors.eobFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Extract text from uploaded file
  const extractTextFromFile = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string || '');
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  // Handle file uploads
  const handlePolicyFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPolicyFile(file);
      try {
        const text = await extractTextFromFile(file);
        setPolicyText(text);
      } catch {
        setPolicyText('');
      }
    }
  };

  const handleEobFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setEobFile(file);
    }
  };

  // Add term to dictionary - Step 1: Get category suggestion and show modal
  const handleAddToDictionary = async () => {
    if (!termExplanation) return;

    setIsLoadingCategory(true);
    setAddTermMessage(null);

    try {
      const token = localStorage.getItem('access_token');
      // Extract just the first paragraph/sentence for short_definition
      const shortDef = termExplanation.definition.split('\n')[0].substring(0, 500);

      // First, get category suggestion from LLM - using insurance-specific endpoint
      const response = await fetch(`${API_BASE_URL}/insurance/suggest-category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          term: termExplanation.term,
          short_definition: shortDef,
          language: i18n.language,
        }),
      });

      const data = await response.json();
      setCategorySuggestion(data);
      setSelectedAddCategory(data.suggested_category);
      setShowCategoryModal(true);
    } catch {
      // Fallback: show modal with default insurance categories
      const defaultCategories = ['costs', 'network', 'process', 'documents', 'plan_types', 'accounts', 'medications', 'coverage', 'rights'];
      setCategorySuggestion({
        term: termExplanation.term,
        suggested_category: 'coverage',
        available_categories: defaultCategories,
        confidence: 'low',
        explanation: t('insuranceGuide:categoryModal.fallbackExplanation', 'Unable to determine best category')
      });
      setSelectedAddCategory('coverage');
      setShowCategoryModal(true);
    } finally {
      setIsLoadingCategory(false);
    }
  };

  // Add term to dictionary - Step 2: Confirm and save with selected category
  const handleConfirmAddTerm = async () => {
    if (!termExplanation || !selectedAddCategory) return;

    setIsAddingTerm(true);
    setAddTermMessage(null);

    try {
      const token = localStorage.getItem('access_token');
      const shortDef = termExplanation.definition.split('\n')[0].substring(0, 500);

      // Using insurance-specific endpoint to add term to insurance dictionary
      const response = await fetch(`${API_BASE_URL}/insurance/add-term`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          term: termExplanation.term,
          short_definition: shortDef,
          category: selectedAddCategory,
          language: i18n.language,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAddTermMessage({
          type: 'success',
          text: data.message || t('insuranceGuide:addedToDictionary')
        });

        // Close the modal
        setShowCategoryModal(false);
        setCategorySuggestion(null);

        // Reload terms to show the newly added term
        loadTerms();

        // Clear success message after 3 seconds
        setTimeout(() => setAddTermMessage(null), 3000);
      } else {
        setAddTermMessage({
          type: 'error',
          text: data.message || t('insuranceGuide:errors.addTermFailed')
        });
      }
    } catch {
      setAddTermMessage({
        type: 'error',
        text: t('insuranceGuide:errors.addTermFailed')
      });
    } finally {
      setIsAddingTerm(false);
    }
  };

  // Cancel adding term
  const handleCancelAddTerm = () => {
    setShowCategoryModal(false);
    setCategorySuggestion(null);
    setSelectedAddCategory('');
  };

  // Handle print
  const handlePrint = () => {
    const content = generateExportContent();
    const htmlContent = marked(content, { async: false }) as string;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head><title>${t('insuranceGuide:title')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
            h1 { font-size: 24px; margin-top: 24px; }
            h2 { font-size: 20px; margin-top: 20px; }
            h3 { font-size: 18px; margin-top: 16px; }
            p { margin: 12px 0; }
            ul, ol { margin-left: 24px; }
            li { margin-bottom: 4px; }
            strong { font-weight: 600; }
            code { background: #f3f4f6; padding: 2px 6px; border-radius: 3px; }
            blockquote { border-left: 4px solid #60a5fa; padding-left: 16px; font-style: italic; }
          </style>
          </head>
          <body>${htmlContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  // Handle Enter key in textarea
  const handleQuestionKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askCoverageQuestion();
    }
  };

  const handleEobKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      interpretEOB();
    }
  };

  // Handle plan type change with Medicaid state prompt
  const handlePlanTypeChange = (value: string) => {
    setPlanType(value);
    if (value === 'Medicaid') {
      setShowStateModal(true);
    }
  };

  const generateExportContent = (): string => {
    let content = `${t('insuranceGuide:title')}\n`;
    content += `${t('common:date')}: ${new Date().toLocaleString()}\n`;
    content += '='.repeat(50) + '\n\n';

    if (activeTab === 'glossary' && termExplanation) {
      content += `## ${t('insuranceGuide:tabs.glossary')}\n\n`;
      content += `### ${termExplanation.term}\n\n`;
      content += `${termExplanation.definition}\n\n`;
      if (termExplanation.how_it_works) {
        content += `**${t('insuranceGuide:howItWorks')}:**\n${termExplanation.how_it_works}\n\n`;
      }
      if (termExplanation.example) {
        content += `**${t('insuranceGuide:example')}:**\n${termExplanation.example}\n\n`;
      }
      if (termExplanation.key_points.length > 0) {
        content += `**${t('insuranceGuide:keyPoints')}:**\n`;
        termExplanation.key_points.forEach((p) => (content += `- ${p}\n`));
        content += '\n';
      }
    } else if (activeTab === 'questions' && coverageAnswer) {
      content += `## ${t('insuranceGuide:tabs.questions')}\n\n`;
      content += `**${t('insuranceGuide:yourQuestion')}:** ${coverageAnswer.question}\n\n`;
      content += `**${t('insuranceGuide:answer')}:**\n${coverageAnswer.answer}\n\n`;
      if (coverageAnswer.explanation) {
        content += `**${t('insuranceGuide:explanation')}:**\n${coverageAnswer.explanation}\n\n`;
      }
    } else if (activeTab === 'costs' && costEstimate) {
      content += `## ${t('insuranceGuide:tabs.costs')}\n\n`;
      content += `**${t('insuranceGuide:procedure')}:** ${costEstimate.procedure}\n\n`;
      content += `${costEstimate.overview}\n\n`;
      if (costEstimate.cost_ranges) {
        content += `**${t('insuranceGuide:costRanges')}:**\n${costEstimate.cost_ranges}\n\n`;
      }
    } else if (activeTab === 'eob' && eobInterpretation) {
      content += `## ${t('insuranceGuide:tabs.eob')}\n\n`;
      content += `**${t('insuranceGuide:summary')}:**\n${eobInterpretation.summary}\n\n`;
      if (eobInterpretation.cost_breakdown) {
        content += `**${t('insuranceGuide:costBreakdown')}:**\n${eobInterpretation.cost_breakdown}\n\n`;
      }
      if (eobInterpretation.what_you_owe) {
        content += `**${t('insuranceGuide:whatYouOwe')}:**\n${eobInterpretation.what_you_owe}\n\n`;
      }
    }

    content += '='.repeat(50) + '\n';
    content += t('insuranceGuide:disclaimer.text') + '\n';

    return content;
  };

  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    const content = exportContent || generateExportContent();
    const title = t('insuranceGuide:title');

    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const blob = await exportAPI.exportDocument({
        content,
        title,
        format: formatMap[format] as 'docx' | 'pdf' | 'txt',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/\s+/g, '_')}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  // Filter terms by search
  const filteredTerms = terms.filter(
    (term) =>
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group terms by category
  const termsByCategory = filteredTerms.reduce((acc, term) => {
    if (!acc[term.category]) {
      acc[term.category] = [];
    }
    acc[term.category].push(term);
    return acc;
  }, {} as Record<string, InsuranceTerm[]>);

  const categoryLabels: Record<string, string> = {
    costs: t('insuranceGuide:categories.costs'),
    network: t('insuranceGuide:categories.network'),
    process: t('insuranceGuide:categories.process'),
    documents: t('insuranceGuide:categories.documents'),
    plan_types: t('insuranceGuide:categories.planTypes'),
    accounts: t('insuranceGuide:categories.accounts'),
    medications: t('insuranceGuide:categories.medications'),
    coverage: t('insuranceGuide:categories.coverage'),
    rights: t('insuranceGuide:categories.rights'),
  };

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'glossary', label: t('insuranceGuide:tabs.glossary'), icon: <BookOpenIcon className="h-5 w-5" /> },
    { id: 'questions', label: t('insuranceGuide:tabs.questions'), icon: <QuestionMarkCircleIcon className="h-5 w-5" /> },
    { id: 'costs', label: t('insuranceGuide:tabs.costs'), icon: <CurrencyDollarIcon className="h-5 w-5" /> },
    { id: 'eob', label: t('insuranceGuide:tabs.eob'), icon: <DocumentTextIcon className="h-5 w-5" /> },
    { id: 'rights', label: t('insuranceGuide:tabs.rights'), icon: <ScaleIcon className="h-5 w-5" /> },
  ];

  return (
    <div className="flex flex-col h-full p-6">
      {/* Loading Overlay for processing */}
      <LoadingOverlay
        isVisible={loading}
        message={t('insuranceGuide:loading.processing', 'Processing...')}
        subMessage={t('insuranceGuide:loading.pleaseWait', 'Analyzing your request')}
      />

      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center mb-4">
          <ShieldCheckIcon className="h-12 w-12 text-blue-500" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('insuranceGuide:title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('insuranceGuide:subtitle')}</p>
      </div>

      {/* Simple Mode Toggle */}
      <div className="flex justify-center mb-4">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={simpleMode}
            onChange={(e) => setSimpleMode(e.target.checked)}
            className="sr-only"
          />
          <div
            className={`relative w-11 h-6 rounded-full transition-colors ${
              simpleMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                simpleMode ? 'translate-x-5' : ''
              }`}
            />
          </div>
          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">{t('insuranceGuide:simpleMode')}</span>
        </label>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Glossary Tab */}
        {activeTab === 'glossary' && (
          <div className="max-w-4xl mx-auto">
            {/* Search */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchTerm.trim()) {
                        e.preventDefault();
                        // If searching for a term not in the list, explain it
                        const matchingTerm = terms.find(
                          (t) => t.term.toLowerCase() === searchTerm.toLowerCase()
                        );
                        if (matchingTerm) {
                          setSelectedTerm(matchingTerm);
                          explainTerm(matchingTerm.term);
                        } else {
                          // Explain the custom search term
                          explainTerm(searchTerm.trim());
                        }
                      }
                    }}
                    placeholder={t('insuranceGuide:searchTerms')}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button
                  onClick={() => {
                    if (searchTerm.trim()) {
                      const matchingTerm = terms.find(
                        (t) => t.term.toLowerCase() === searchTerm.toLowerCase()
                      );
                      if (matchingTerm) {
                        setSelectedTerm(matchingTerm);
                        explainTerm(matchingTerm.term);
                      } else {
                        explainTerm(searchTerm.trim());
                      }
                    }
                  }}
                  disabled={!searchTerm.trim()}
                  className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={t('insuranceGuide:glossary.explain')}
                >
                  <PaperAirplaneIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Terms List */}
              <div className="space-y-4">
                {Object.entries(termsByCategory).map(([category, categoryTerms]) => (
                  <div key={category} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <button
                      onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {categoryLabels[category] || category} ({categoryTerms.length})
                      </span>
                      {expandedCategory === category ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCategory(null);
                          }}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          {t('insuranceGuide:glossary.back', 'Back')}
                        </button>
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    {expandedCategory === category && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        {categoryTerms.map((term) => (
                          <button
                            key={term.id}
                            onClick={() => {
                              setSelectedTerm(term);
                              explainTerm(term.term);
                            }}
                            className={`w-full p-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                              selectedTerm?.id === term.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">{term.term}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                              {term.definition}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Term Explanation */}
              <div>
                {loading ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common:loading')}</p>
                  </div>
                ) : termExplanation ? (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">{termExplanation.term}</h3>
                      <button
                        onClick={handleAddToDictionary}
                        disabled={isAddingTerm || isLoadingCategory}
                        className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingCategory
                          ? t('insuranceGuide:common.loading')
                          : t('insuranceGuide:addToDictionary')}
                      </button>
                    </div>
                    <div className="flex items-center justify-end space-x-2">
                      <ExportDropdownMenu onExport={handleExport} onPrint={handlePrint} showPrint={true} />
                    </div>
                    {addTermMessage && (
                      <div className={`px-3 py-2 rounded-lg text-sm ${
                        addTermMessage.type === 'success'
                          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      }`}>
                        {addTermMessage.text}
                      </div>
                    )}

                    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-white">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{termExplanation.definition}</ReactMarkdown>
                    </div>

                    {termExplanation.how_it_works && (
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          {t('insuranceGuide:howItWorks')}
                        </h4>
                        <p className="text-gray-700 dark:text-gray-300">{termExplanation.how_it_works}</p>
                      </div>
                    )}

                    {termExplanation.example && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                          {t('insuranceGuide:example')}
                        </h4>
                        <p className="text-blue-700 dark:text-blue-400">{termExplanation.example}</p>
                      </div>
                    )}

                    {termExplanation.key_points.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          {t('insuranceGuide:keyPoints')}
                        </h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                          {termExplanation.key_points.map((point, idx) => (
                            <li key={idx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {termExplanation.questions_to_ask.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                          {t('insuranceGuide:questionsToAsk')}
                        </h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                          {termExplanation.questions_to_ask.map((q, idx) => (
                            <li key={idx}>{q}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-300">
                      {termExplanation.disclaimer}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center text-gray-500 dark:text-gray-400">
                    {t('insuranceGuide:selectTerm')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Coverage Questions Tab */}
        {activeTab === 'questions' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('insuranceGuide:askQuestion')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('insuranceGuide:planType')} ({t('common:optional')})
                  </label>
                  <select
                    value={planType}
                    onChange={(e) => handlePlanTypeChange(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('insuranceGuide:selectPlanType')}</option>
                    <option value="HMO">{t('insuranceGuide:planTypes.hmo')}</option>
                    <option value="PPO">{t('insuranceGuide:planTypes.ppo')}</option>
                    <option value="HDHP">{t('insuranceGuide:planTypes.hdhp')}</option>
                    <option value="EPO">{t('insuranceGuide:planTypes.epo')}</option>
                    <option value="POS">{t('insuranceGuide:planTypes.pos')}</option>
                    <option value="Medicare">{t('insuranceGuide:planTypes.medicare')}</option>
                    <option value="Medicaid">{t('insuranceGuide:planTypes.medicaid')}</option>
                  </select>
                  {planType === 'Medicaid' && medicaidState && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {t('insuranceGuide:medicaidState')}: {medicaidState}
                    </p>
                  )}
                </div>

                {/* Policy Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('insuranceGuide:uploadPolicy')} ({t('common:optional')})
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t('insuranceGuide:uploadPolicyDesc')}
                  </p>
                  <input
                    ref={policyFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handlePolicyFileUpload}
                    className="hidden"
                  />
                  {policyFile ? (
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <DocumentArrowUpIcon className="h-5 w-5 text-blue-500" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{policyFile.name}</span>
                      </div>
                      <button
                        onClick={() => { setPolicyFile(null); setPolicyText(''); }}
                        className="text-gray-500 hover:text-red-500"
                        title={t('insuranceGuide:removeFile')}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => policyFileInputRef.current?.click()}
                      className="flex items-center space-x-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
                    >
                      <PaperClipIcon className="h-5 w-5" />
                      <span>{t('insuranceGuide:uploadPolicy')}</span>
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('insuranceGuide:yourQuestion')}
                  </label>
                  <div className="relative">
                    <textarea
                      ref={questionTextareaRef}
                      value={coverageQuestion}
                      onChange={(e) => setCoverageQuestion(e.target.value)}
                      onKeyDown={handleQuestionKeyDown}
                      placeholder={t('insuranceGuide:questionPlaceholder')}
                      rows={3}
                      className="w-full p-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    <button
                      onClick={askCoverageQuestion}
                      disabled={loading || !coverageQuestion.trim()}
                      className="absolute right-3 bottom-3 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={t('insuranceGuide:getAnswer')}
                    >
                      {loading ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <ArrowRightIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {coverageAnswer && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('insuranceGuide:answer')}
                  </h3>
                  <ExportDropdownMenu onExport={handleExport} onPrint={handlePrint} showPrint={true} />
                </div>

                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-white">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{coverageAnswer.answer}</ReactMarkdown>
                </div>

                {coverageAnswer.explanation && (
                  <div className="text-gray-700 dark:text-gray-300">{coverageAnswer.explanation}</div>
                )}

                {coverageAnswer.factors.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('insuranceGuide:itDependsOn')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {coverageAnswer.factors.map((f, idx) => (
                        <li key={idx}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {coverageAnswer.steps_to_take.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('insuranceGuide:stepsToTake')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {coverageAnswer.steps_to_take.map((s, idx) => (
                        <li key={idx}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-300">
                  {coverageAnswer.disclaimer}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cost Estimator Tab */}
        {activeTab === 'costs' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('insuranceGuide:costEstimator')}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('insuranceGuide:procedure')}
                  </label>
                  <input
                    type="text"
                    value={procedure}
                    onChange={(e) => setProcedure(e.target.value)}
                    placeholder={t('insuranceGuide:procedurePlaceholder')}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasMetDeductible}
                      onChange={(e) => setHasMetDeductible(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{t('insuranceGuide:metDeductible')}</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inNetwork}
                      onChange={(e) => setInNetwork(e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-700 dark:text-gray-300">{t('insuranceGuide:inNetwork')}</span>
                  </label>
                </div>

                <button
                  onClick={estimateCost}
                  disabled={loading || !procedure.trim()}
                  className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('common:loading') : t('insuranceGuide:estimateCost')}
                </button>
              </div>
            </div>

            {costEstimate && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('insuranceGuide:costEstimateFor')} {costEstimate.procedure}
                  </h3>
                  <ExportDropdownMenu onExport={handleExport} onPrint={handlePrint} showPrint={true} />
                </div>

                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-white">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{costEstimate.overview}</ReactMarkdown>
                </div>

                {costEstimate.cost_ranges && (
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 dark:text-green-300 mb-2">
                      {t('insuranceGuide:costRanges')}
                    </h4>
                    <div className="text-green-700 dark:text-green-400 whitespace-pre-wrap">
                      {costEstimate.cost_ranges}
                    </div>
                  </div>
                )}

                {costEstimate.before_procedure.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('insuranceGuide:beforeProcedure')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {costEstimate.before_procedure.map((s, idx) => (
                        <li key={idx}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {costEstimate.negotiation_tips.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('insuranceGuide:negotiationTips')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {costEstimate.negotiation_tips.map((t, idx) => (
                        <li key={idx}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-300">
                  {costEstimate.disclaimer}
                </div>
              </div>
            )}
          </div>
        )}

        {/* EOB Interpreter Tab */}
        {activeTab === 'eob' && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('insuranceGuide:eobInterpreter')}
              </h3>

              <div className="space-y-4">
                {/* EOB File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('insuranceGuide:uploadEOB')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t('insuranceGuide:uploadEOBDesc')}
                  </p>
                  <input
                    ref={eobFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleEobFileUpload}
                    className="hidden"
                  />
                  {eobFile ? (
                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        <DocumentArrowUpIcon className="h-5 w-5 text-blue-500" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">{eobFile.name}</span>
                      </div>
                      <button
                        onClick={() => setEobFile(null)}
                        className="text-gray-500 hover:text-red-500"
                        title={t('insuranceGuide:removeFile')}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => eobFileInputRef.current?.click()}
                      className="flex items-center space-x-2 px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:border-blue-500 hover:text-blue-500"
                    >
                      <PaperClipIcon className="h-5 w-5" />
                      <span>{t('insuranceGuide:uploadEOB')}</span>
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('insuranceGuide:orPasteBelow')}
                  </label>
                  <div className="relative">
                    <textarea
                      ref={eobTextareaRef}
                      value={eobText}
                      onChange={(e) => setEobText(e.target.value)}
                      onKeyDown={handleEobKeyDown}
                      placeholder={t('insuranceGuide:eobPlaceholder')}
                      rows={8}
                      disabled={!!eobFile}
                      className="w-full p-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm disabled:opacity-50"
                    />
                    <button
                      onClick={interpretEOB}
                      disabled={loading || (!eobText.trim() && !eobFile)}
                      className="absolute right-3 bottom-3 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={t('insuranceGuide:interpretEOB')}
                    >
                      {loading ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                      ) : (
                        <ArrowRightIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {eobInterpretation && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t('insuranceGuide:eobSummary')}
                  </h3>
                  <ExportDropdownMenu onExport={handleExport} onPrint={handlePrint} showPrint={true} />
                </div>

                <div className="prose prose-sm dark:prose-invert max-w-none text-gray-900 dark:text-white">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{eobInterpretation.summary}</ReactMarkdown>
                </div>

                {eobInterpretation.cost_breakdown && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">
                      {t('insuranceGuide:costBreakdown')}
                    </h4>
                    <div className="text-blue-700 dark:text-blue-400 whitespace-pre-wrap">
                      {eobInterpretation.cost_breakdown}
                    </div>
                  </div>
                )}

                {eobInterpretation.what_you_owe && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800 dark:text-orange-300 mb-2">
                      {t('insuranceGuide:whatYouOwe')}
                    </h4>
                    <div className="text-orange-700 dark:text-orange-400">{eobInterpretation.what_you_owe}</div>
                  </div>
                )}

                {eobInterpretation.to_verify.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('insuranceGuide:toVerify')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {eobInterpretation.to_verify.map((v, idx) => (
                        <li key={idx}>{v}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {eobInterpretation.next_steps.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('insuranceGuide:nextSteps')}
                    </h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {eobInterpretation.next_steps.map((s, idx) => (
                        <li key={idx}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-300">
                  {eobInterpretation.disclaimer}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rights & Protections Tab */}
        {activeTab === 'rights' && (
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Patient Rights */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('insuranceGuide:patientRights')}
                </h3>
                <div className="space-y-4">
                  {rights.map((right) => (
                    <div key={right.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{right.title}</h4>
                      <p className="text-gray-700 dark:text-gray-300 text-sm mb-3">{right.description}</p>
                      <div className="text-sm">
                        <span className="font-medium text-gray-600 dark:text-gray-400">
                          {t('insuranceGuide:appliesTo')}:{' '}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">{right.applies_to.join(', ')}</span>
                      </div>
                      <div className="mt-2 text-sm bg-green-50 dark:bg-green-900/20 rounded p-2 text-green-700 dark:text-green-400">
                        <strong>{t('insuranceGuide:whatToDo')}:</strong> {right.what_to_do}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plan Types */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('insuranceGuide:planTypesCompare')}
                </h3>
                <div className="space-y-4">
                  {planTypes.map((plan) => (
                    <div key={plan.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{plan.name}</h4>

                      <div className="mb-2">
                        <span className="text-sm font-medium text-green-600 dark:text-green-400">
                          {t('insuranceGuide:pros')}:
                        </span>
                        <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 ml-2">
                          {plan.pros.slice(0, 3).map((pro, idx) => (
                            <li key={idx}>{pro}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-2">
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          {t('insuranceGuide:cons')}:
                        </span>
                        <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 ml-2">
                          {plan.cons.slice(0, 3).map((con, idx) => (
                            <li key={idx}>{con}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="text-sm bg-blue-50 dark:bg-blue-900/20 rounded p-2 text-blue-700 dark:text-blue-400">
                        <strong>{t('insuranceGuide:bestFor')}:</strong> {plan.best_for}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-sm text-yellow-800 dark:text-yellow-300">
          <strong>{t('insuranceGuide:disclaimer.title')}:</strong> {t('insuranceGuide:disclaimer.text')}
        </p>
      </div>

      {/* Medicaid State Selection Modal */}
      {showStateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('insuranceGuide:selectState')}
            </h3>
            <select
              value={medicaidState}
              onChange={(e) => setMedicaidState(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
            >
              <option value="">{t('insuranceGuide:selectState')}</option>
              {US_STATES.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowStateModal(false);
                  if (!medicaidState) {
                    setPlanType('');
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={() => setShowStateModal(false)}
                disabled={!medicaidState}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {t('common:buttons.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Selection Modal for Add to Dictionary */}
      {showCategoryModal && categorySuggestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('insuranceGuide:categoryModal.title', 'Select Category')}
              </h3>
              <button
                onClick={handleCancelAddTerm}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('insuranceGuide:categoryModal.description', 'Choose a category for this term:')}
              </p>

              {/* Term Info */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
                <p className="font-medium text-gray-900 dark:text-white">{categorySuggestion.term}</p>
              </div>

              {/* AI Suggestion */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('insuranceGuide:categoryModal.aiSuggestion', 'AI Suggested Category')}
                </label>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    categorySuggestion.confidence === 'high'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : categorySuggestion.confidence === 'medium'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                  }`}>
                    {categoryLabels[categorySuggestion.suggested_category] || categorySuggestion.suggested_category}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({categorySuggestion.confidence} {t('insuranceGuide:categoryModal.confidence', 'confidence')})
                  </span>
                </div>
                {categorySuggestion.explanation && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {categorySuggestion.explanation}
                  </p>
                )}
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('insuranceGuide:categoryModal.selectCategory', 'Category')}
                </label>
                <select
                  value={selectedAddCategory}
                  onChange={(e) => setSelectedAddCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {categorySuggestion.available_categories.map((category) => (
                    <option key={category} value={category}>
                      {categoryLabels[category] || category} {category === categorySuggestion.suggested_category ? `(${t('insuranceGuide:categoryModal.recommended', 'recommended')})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Add term error message in modal */}
              {addTermMessage && addTermMessage.type === 'error' && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 text-sm">
                  {addTermMessage.text}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={handleCancelAddTerm}
                disabled={isAddingTerm}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {t('insuranceGuide:categoryModal.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirmAddTerm}
                disabled={isAddingTerm || !selectedAddCategory}
                className="px-4 py-2 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAddingTerm
                  ? t('insuranceGuide:categoryModal.adding', 'Adding...')
                  : t('insuranceGuide:categoryModal.confirm', 'Add to Dictionary')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
